import { createSelector } from '@reduxjs/toolkit';
import { AutomationExecutionNodeStatusEnum, AutomationStatus } from '@repo/utils';
import { NodeType } from 'automations/data/nodeTypes';
import { type RootState } from 'automations/store';
import { AutomationMode } from 'automations/types/automation';
import { ConditionTypes, Triggers } from 'automations/types/automationConfig';
import {
  type CommonNodeData, type ElseNodeType,
  type LeadActivityCreateNodeData, type LeadActivityUpdateNodeData, type TriggerNodeType,
} from 'automations/types/nodeTypes';
import { didNodeSyncToServer } from 'automations/utils/node';
import { getParsedTriggerActivityConfig } from 'automations/utils/triggeredActivityConfig';

import { automationModeSelector, automationStatusSelector } from './automationConfig';

const baseSelector = (state: RootState) => state.automations.flowNodes;

export const nodesSelector = createSelector(baseSelector, (state) => state.nodes);
export const edgesSelector = createSelector(baseSelector, (state) => state.edges);

export const isTriggerNodePresentSelector = createSelector(
  nodesSelector,
  (nodes) => nodes.length > 0 && nodes[0]?.type === NodeType.trigger,
);

export const childrenEdgesSelector = (nodeId: string) => createSelector(
  edgesSelector,
  (edges) => edges.filter((edge) => edge.source === nodeId),
);

export const childrenNodesSelector = (nodeId: string) => createSelector(
  nodesSelector,
  childrenEdgesSelector(nodeId),
  (nodes, edges) => nodes.filter((node) => edges.some((edge) => edge.target === node.id)),
);

// get childern count for a node
export const childrenCountSelector = (nodeId: string) => createSelector(
  childrenEdgesSelector(nodeId),
  (edges) => edges.length,
);

export const getParentIdSelector = (nodeId: string) => createSelector(
  edgesSelector,
  (edges) => edges.find((edge) => edge.target === nodeId)?.source,
);

export const isActivityRelatedTriggerNodeSelector = createSelector(
  nodesSelector,
  (nodes) => {
    const triggerNode = nodes.find((node) => node.type === NodeType.trigger);
    return triggerNode?.data.nodeSubType === Triggers.leadActivityCreate
      || triggerNode?.data.nodeSubType === Triggers.leadActivityUpdate;
  },
);

export const isDealRelatedTriggerNodeSelector = createSelector(
  nodesSelector,
  (nodes) => {
    const triggerNode = nodes.find((node) => node.type === NodeType.trigger);
    return triggerNode?.data.nodeSubType === Triggers.dealCreate
      || triggerNode?.data.nodeSubType === Triggers.dealUpdate;
  },
);

export const getTriggerNodeSelector = createSelector(
  nodesSelector,
  (nodes) => nodes.find((node) => node.type === NodeType.trigger),
);

export const getAdvancedFilterAdditionalKeysSelector = createSelector(
  isActivityRelatedTriggerNodeSelector,
  getTriggerNodeSelector,
  (isActivityRelatedTriggerNode, triggerNode) => {
    if (!isActivityRelatedTriggerNode || !triggerNode) return [];

    const { nodeSubType } = triggerNode.data;

    if (nodeSubType === Triggers.leadActivityCreate) {
      const triggerNodeData = triggerNode.data as LeadActivityCreateNodeData;
      return [getParsedTriggerActivityConfig(triggerNodeData.additionalOptions?.newActivity?.criterias ?? [])];
    }

    if (nodeSubType === Triggers.leadActivityUpdate) {
      const triggerNodeData = triggerNode.data as LeadActivityUpdateNodeData;
      return [getParsedTriggerActivityConfig([
        { activityId: triggerNodeData.additionalOptions?.updatedActivity?.activityId },
      ])];
    }

    return [];
  },
);

export const getElseNodeSelector = (parentNodeId: string) => createSelector(
  nodesSelector,
  edgesSelector,
  (nodes, edges) => nodes.find(
    (node) => edges.some((edge) => edge.source === parentNodeId && edge.target === node.id)
      && node.data?.nodeSubType === ConditionTypes.else,
  ) as ElseNodeType | undefined,
);

/**
 * Selector to check if the trigger node is a activity related trigger node
 * If it is, and the activity id is only (1) in count (for leadActivityCreate)
 * or is a single activity (for leadActivityUpdate),
 * Then returns the activity id else returns undefined
 */
export const triggeredActivityForMailMergeSelector = createSelector(
  getTriggerNodeSelector,
  isActivityRelatedTriggerNodeSelector,
  (triggerNode, isActivityRelatedTriggerNode) => {
    if (!isActivityRelatedTriggerNode || !triggerNode) return undefined;

    const { nodeSubType } = triggerNode.data;

    if (nodeSubType === Triggers.leadActivityCreate) {
      const triggerNodeData = triggerNode.data as LeadActivityCreateNodeData;
      const activityCriterias = triggerNodeData?.additionalOptions?.newActivity?.criterias;
      if (activityCriterias?.length === 1) return activityCriterias[0]?.activityId;
    } else if (nodeSubType === Triggers.leadActivityUpdate) {
      const triggerNodeData = triggerNode.data as LeadActivityUpdateNodeData;
      return triggerNodeData?.additionalOptions?.updatedActivity?.activityId;
    }

    return undefined;
  },
);

export const hoveredEdgeIdSelector = createSelector(baseSelector, (state) => state.hoveredEdgeId);

export const nodeSelector = (nodeId: string) => createSelector(
  nodesSelector,
  (nodes) => nodes.find((node) => node.id === nodeId),
);

export const canAddNodesSelector = (nodeId: string) => createSelector(
  nodeSelector(nodeId),
  automationStatusSelector,
  childrenCountSelector(nodeId),
  (node, automationStatus, childrenCount) => {
    if (
      !node || !didNodeSyncToServer(node)
    ) {
      return false;
    }

    // Don't allow adding nodes if:
    // 1. Unpublished with existing children
    // 2. Published or archived status
    if (automationStatus === AutomationStatus.unpublished && childrenCount > 0) {
      return false;
    }

    return ![AutomationStatus.published, AutomationStatus.archived].includes(automationStatus!);
  },
);

export const canEditContentSelector = (nodeId: string) => createSelector(
  nodeSelector(nodeId),
  automationStatusSelector,
  automationModeSelector,
  (node, automationStatus, automationMode) => {
    if (
      automationMode === AutomationMode.liveEdit
      && node?.data?.nodeType === NodeType.action
    ) {
      return true;
    }

    if (automationStatus === AutomationStatus.published) {
      return false;
    }

    return true;
  },
);

export const totalLeadsEnteredCountSelector = createSelector(
  nodesSelector,
  (nodes) => {
    const triggerNode = nodes.find((node) => node.type === NodeType.trigger) as TriggerNodeType;
    return triggerNode?.data?.successExecutionCounts;
  },
);

export const totalLeadsCompletedCountSelector = createSelector(
  nodesSelector,
  edgesSelector,
  (nodes, edges) => {
    const nodesWithoutOutgoingEdges = nodes.filter((node) => !edges.some((edge) => edge.source === node.id));
    return nodesWithoutOutgoingEdges.reduce(
      (acc, node) => acc + ((node.data as CommonNodeData)?.successExecutionCounts ?? 0),
      0,
    );
  },
);

export const allowAnimationSelector = (nodeId: string) => createSelector(
  nodeSelector(nodeId),
  childrenNodesSelector(nodeId),
  (node, children) => {
    if (node?.data?.nodeSubType !== ConditionTypes.if) return true;

    if (
      [AutomationExecutionNodeStatusEnum.SUCCESS,
        AutomationExecutionNodeStatusEnum.FAILED].includes(node?.data?.executionStatus as any)
    ) {
      return children.some((child) => child.data?.executionStatus !== AutomationExecutionNodeStatusEnum.PENDING);
    }

    return false;
  },
);
